//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

// Dropbox Chooser Docs: https://www.dropbox.com/developers/chooser

import { useQuery } from '@tanstack/react-query';
import equal from 'fast-deep-equal';
import { useRef } from 'react';

import {
  BYTES_PER_MB,
  NORMAL_VIDEO_IMPORT_LIMIT_MB,
} from '@content-drawer/contentDrawerConstants';
import {
  DROPBOX_SCRIPT_ID,
  DROPBOX_SDK_URL,
} from '@content-drawer/dropbox/dropboxConstants';
import { Dropbox, DropboxFile } from '@content-drawer/dropbox/dropboxModels';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { SUPPORTED_MEDIA_EXTENSIONS_WITH_DOT_PREFIX } from '@utils/mimeTypes';

declare global {
  interface Window {
    Dropbox: Dropbox | undefined;
  }
}

const loadDropboxScript = () =>
  // No return value is needed in Promise, but returning a boolean since react-query does not allow to return undefined
  new Promise<boolean>((resolve, reject) => {
    if (!document.getElementById(DROPBOX_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = DROPBOX_SDK_URL;
      script.id = DROPBOX_SCRIPT_ID;
      script.dataset.appKey = CURALATE_ENV.DROPBOX_APP_KEY;
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = reject;
      document.body.appendChild(script);
    } else {
      resolve(true);
    }
  });

const useDropboxScriptLoaderQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Dropbox,
      QueryKeyConstants.DropboxScriptLoader,
    ],
    queryFn: loadDropboxScript,
    staleTime: Infinity,
  });
};

const openDropboxChooser = (
  onSuccess: (files: DropboxFile[]) => void,
  onCancel?: () => void,
) => {
  window.Dropbox?.choose({
    cancel: () => onCancel?.(),
    extensions: [...SUPPORTED_MEDIA_EXTENSIONS_WITH_DOT_PREFIX],
    folderselect: false,
    linkType: 'direct',
    multiselect: true,
    sizeLimit: NORMAL_VIDEO_IMPORT_LIMIT_MB * BYTES_PER_MB,
    success: onSuccess,
  });
};

export const useDropboxChooser = (
  onSuccess: (files: DropboxFile[]) => void,

  /**
   * Warning! Dropbox may call this function multiple times even for one cancel event.
   * Please handle the duplicate calls appropriately.
   */
  onCancel?: () => void,
) => {
  const { isFetched: isScriptLoaded, refetch } = useDropboxScriptLoaderQuery();
  
  // Sometimes, the onSuccess callback passed to Dropbox is called multiple times in quick succession,
  // leading to duplicate file uploads. This appears to be a bug in the Dropbox Chooser.
  // To fix this, we store the values from the last call and compare them to the current call to to decide whether to invoke the actual callback.
  const previousFilesRef = useRef<DropboxFile[]>([]);

  const chooseFromDropbox = () => {
    const handleSuccess = (files: DropboxFile[]) => {
      if (!equal(files, previousFilesRef.current)) {
        previousFilesRef.current = files;
        onSuccess(files);
      }
    };

    if (!isScriptLoaded) {
      void refetch().then(() => {
        openDropboxChooser(handleSuccess, onCancel);
      });
    } else {
      openDropboxChooser(handleSuccess, onCancel);
    }
  };

  return { chooseFromDropbox };
};
